import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface JournalAnalyticsProps {
  moodData: Array<{
    date: string;
    mood: number;
  }>;
  symptomsFrequency?: Record<string, number>;
}

export const JournalAnalytics = ({ moodData, symptomsFrequency }: JournalAnalyticsProps) => {
  const { data: breathingData } = useQuery({
    queryKey: ["breathing-measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("breathing_measurements")
        .select("*")
        .order("timestamp", { ascending: true });
      
      if (error) throw error;
      return data?.map(measurement => ({
        date: new Date(measurement.timestamp).toLocaleDateString('fr-FR'),
        rate: measurement.breathing_rate,
        regularity: measurement.regularity_score
      }));
    }
  });

  const { data: expressionData } = useQuery({
    queryKey: ["expression-measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expression_measurements")
        .select("*")
        .order("timestamp", { ascending: true });
      
      if (error) throw error;
      return data?.map(measurement => ({
        date: new Date(measurement.timestamp).toLocaleDateString('fr-FR'),
        emotion: measurement.emotion,
        confidence: measurement.confidence_score
      }));
    }
  });

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Évolution de l'humeur</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={moodData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="mood" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={{ fill: "#2563eb" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mesures de respiration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={breathingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" orientation="left" stroke="#2563eb" />
                <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                <Tooltip />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="rate" 
                  name="Respirations/min"
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={{ fill: "#2563eb" }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="regularity" 
                  name="Régularité (%)"
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: "#10b981" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Analyse des expressions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={expressionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="confidence" 
                  name="Confiance (%)"
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={{ fill: "#2563eb" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
